import type {
  ICollaboratorBenefitsResponse,
  ICollaboratorInsightsData,
  ICollaboratorInvitationLink,
  ICompanyInfoResponse,
  ICustomResponse,
  IExternalFileResponse,
  IFileResponse
} from '../../../interfaces/businessData';
import type {
  ICompanyContract,
  ICollaboratorDependents,
  IValidateMembersResponse,
  IValidatePayrollResponse,
  IValidatePayrollSuccessResponse,
  IUpdateDataRF,
  ICollaboratorDataRF,
  ICollaboratorInsuranceDetail,
  IInsuranceCertificate,
  IMemberFRCardBalances,
  IMemberFRCycleConsumptions
} from '../../../interfaces/collaborators';
import type {
  IValidateDependentsFileRequest,
  IValidateDependentsFileResponse,
  IValidateFileRequest,
  IValidateFileResponse
} from '../../../interfaces/manageCollaborators';
import type { ICompanyPlanResponse } from '../../../interfaces/plans';
import type { ICompanyRequestDetail } from '../../../interfaces/requests';
import type {
  IAgreementsResponse,
  ICollaboratorsResponse,
  ICompanyRequestsResponse,
  IMembersResponse,
  IOpportunityResponse,
  IRequestFile,
  IUnsubscribedCollaboratorsResponse,
  IUnsubscribedMembersResponse
} from '../../../interfaces/responseInterface';
import {
  AVAIABLE_OPPORTUNITIES_MOCK_RESPONSE,
  COLLABORATOR_BENEFITS_MOCK_RESPONSE,
  COLLABORATOR_FILE_MOCK_RESPONSE,
  COLLABORATOR_INSIGHTS_MOCK_RESPONSE,
  COLLABORATOR_INVITATION_LINK_MOCK_RESPONSE,
  COLLABORATOR_MOCK_RESPONSE,
  MEMBERS_MOCK_RESPONSE,
  MOCK_COLLABORATOR_DEPENDENTS,
  MOCK_COLLABORATOR_DETAILS_RF_DATA,
  MOCK_COLLABORATOR_INSURANCE_DETAIL,
  MOCK_MEMBER_FR_CARD_BALANCES,
  MOCK_MEMBER_FR_CYCLE_CONSUMPTIONS,
  MOCK_DOWNLOAD_EXTERNAL_FILE_RESPONSE,
  MOCK_DEPENDENTS_BY_FILE_VALIDATION,
  REQUEST_FILE_MOCK_RESPONSE,
  UNSUBSCRIBED_COLLABORATORS_MOCK_RESPONSE,
  UNSUBSCRIBED_MEMBERS_MOCK_RESPONSE,
  VALIDATE_COLLABORATORS_MOCK_REPONSE,
  VALIDATE_PAYROLL_ERROR_MOCK_RESPONSE,
  VALIDATE_PAYROLL_SUCCESS_MOCK_RESPONSE,
  MOCK_INSURANCE_CERTIFICATES
} from '../../../mocks/collaborators';
import {
  COMPANY_AGREEMENTS_RESPONSE_MOCK,
  COMPANY_METRICS_RESPONSE_MOCK,
  MOCK_COMPANY_CONTRACT,
  MOCK_COMPANY_PLANS
} from '../../../mocks/company';
import { DEFAULT_CUSTOM_RESPONSE_MOCK } from '../../../mocks/customResponse';
import { MOCK_DOWNLOAD_FILE_RESPONSE } from '../../../mocks/metrics';
import { COMPANY_REQUEST_DETAIL_MOCK, COMPANY_REQUEST_RESPONSE_MOCK } from '../../../mocks/requests';
import { HttpService, HttpServiceServer } from '../../http/httpService';

export class CollaboratorsCompanies {
  static async getFilteredCollaborators(
    companyId: string,
    searchValue?: string,
    isActive?: string
  ): Promise<ICollaboratorsResponse> {
    const response = await HttpService.get<ICollaboratorsResponse>(
      `companies/${companyId}/collaborators`,
      { params: { searchValue, isActive } },
      COLLABORATOR_MOCK_RESPONSE
    );
    return response.data;
  }

  static async getFilteredMembers(
    companyId: string,
    searchValue?: string,
    status?: string,
    playerId?: string
  ): Promise<IMembersResponse> {
    const params = playerId ? { playerId } : { searchValue, status };
    const response = await HttpServiceServer.get<IMembersResponse>(
      `companies/${companyId}/members`,
      { params },
      MEMBERS_MOCK_RESPONSE
    );
    return response.data;
  }

  static async getCollaboratorsFile(companyId: string): Promise<IFileResponse> {
    const response = await HttpService.get<IFileResponse>(
      `companies/${companyId}/reports/collaborators`,
      {},
      COLLABORATOR_FILE_MOCK_RESPONSE
    );
    return response.data;
  }

  static async sendMassCollaboratorEmail(companyId: string): Promise<string> {
    const response = await HttpService.post<ICustomResponse>(
      `companies/${companyId}/onboarding-email`,
      {},
      {},
      DEFAULT_CUSTOM_RESPONSE_MOCK
    );
    const customResponse: ICustomResponse = response.data;
    return customResponse.message;
  }

  static async getCompanyInfo(companyId: string, language?: string): Promise<ICompanyInfoResponse> {
    const languageParam = language || 'es';
    const response = await HttpServiceServer.get<ICompanyInfoResponse>(
      `companies/${companyId}/`,
      { params: { language: languageParam } },
      COMPANY_METRICS_RESPONSE_MOCK
    );
    return response.data;
  }

  static async getCompanyAgreements(companyId: string): Promise<IAgreementsResponse> {
    const response = await HttpService.get<IAgreementsResponse>(
      `companies/${companyId}/agreements/`,
      {},
      COMPANY_AGREEMENTS_RESPONSE_MOCK
    );
    return response.data;
  }

  static async getCompanyTemplate(
    companyId: string,
    agreementCode: string,
    templateType: string
  ): Promise<IFileResponse> {
    const response = await HttpService.get<IFileResponse>(
      `companies/${companyId}/bulk-load/templates/${agreementCode}/${templateType}`,
      {},
      COLLABORATOR_FILE_MOCK_RESPONSE
    );
    return response.data;
  }

  static async sendCollaboratorsFile(
    companyId: string,
    agreementCode: string,
    type: 'subscribe' | 'unsubscribe',
    email: string,
    data: string
  ): Promise<void> {
    const body = {
      agreementId: agreementCode,
      email: email,
      base64: data
    };
    await HttpService.post(
      `companies/${companyId}/requests/collaborators/bulk-load/${type}`,
      body,
      {},
      DEFAULT_CUSTOM_RESPONSE_MOCK
    );
  }

  static async getCompanyTemplateNE(companyId: string, countryCode: string): Promise<IFileResponse> {
    const response = await HttpService.get<IFileResponse>(
      `companies/${companyId}/members/templates/${countryCode}`,
      {},
      COLLABORATOR_FILE_MOCK_RESPONSE
    );
    return response.data;
  }

  static async validatePayroll(companyId: string, planId: string, base64: string): Promise<IValidatePayrollResponse> {
    const body = {
      planId: planId,
      base64: base64
    };
    const response = await HttpService.post<IValidatePayrollResponse>(
      `companies/${companyId}/members/validate-payroll`,
      body,
      {},
      VALIDATE_PAYROLL_ERROR_MOCK_RESPONSE
    );
    return response.data;
  }

  static async sendCollaboratorsFileData(
    companyId: string,
    planId: string,
    data: IValidatePayrollSuccessResponse[]
  ): Promise<void> {
    const body = {
      planId: planId,
      members: data
    };
    await HttpService.post(`companies/${companyId}/members/file-subscription`, body, {}, DEFAULT_CUSTOM_RESPONSE_MOCK);
  }

  static async getCompanyRequests(companyId: string): Promise<ICompanyRequestsResponse> {
    const response = await HttpService.get<ICompanyRequestsResponse>(
      `companies/${companyId}/requests`,
      {},
      COMPANY_REQUEST_RESPONSE_MOCK
    );
    return response.data;
  }

  static async getUnsubscribedCollaborators(
    companyId: string,
    searchValue?: string
  ): Promise<IUnsubscribedCollaboratorsResponse> {
    const response = await HttpService.get<IUnsubscribedCollaboratorsResponse>(
      `companies/${companyId}/collaborators/unsubscribed`,
      { params: { searchValue } },
      UNSUBSCRIBED_COLLABORATORS_MOCK_RESPONSE
    );
    return response.data;
  }

  static async getUnsubscribedMembers(companyId: string, searchValue?: string): Promise<IUnsubscribedMembersResponse> {
    const response = await HttpServiceServer.get<IUnsubscribedMembersResponse>(
      `companies/${companyId}/members/unsubscribed`,
      { params: { searchValue } },
      UNSUBSCRIBED_MEMBERS_MOCK_RESPONSE
    );
    return response.data;
  }

  static async getRequestFile(companyId: string, requestId: string): Promise<IRequestFile> {
    const response = await HttpService.get<IRequestFile>(
      `companies/${companyId}/requests/${requestId}/attachments`,
      {},
      REQUEST_FILE_MOCK_RESPONSE
    );
    return response.data;
  }

  static async getOpportunities(): Promise<IOpportunityResponse> {
    const response = await HttpService.get<IOpportunityResponse>(
      'pre-onboarding/opportunities',
      {},
      AVAIABLE_OPPORTUNITIES_MOCK_RESPONSE
    );
    return response.data;
  }

  static async sendOnboardingCollaboratorEmail(companyId: string, collaboratorId: string): Promise<string> {
    const response = await HttpService.post<ICustomResponse>(
      `companies/${companyId}/collaborator/${collaboratorId}/onboarding_email`,
      {},
      {},
      DEFAULT_CUSTOM_RESPONSE_MOCK
    );
    const customResponse: ICustomResponse = response.data;
    return customResponse.message;
  }

  static async resendOnboardingCollaboratorEmails(companyId: string, memberEmails: string[]): Promise<string> {
    const body = { members: memberEmails };
    const response = await HttpServiceServer.post<ICustomResponse>(
      `companies/${companyId}/members/onboarding-email/resend`,
      body,
      {},
      DEFAULT_CUSTOM_RESPONSE_MOCK
    );
    const customResponse: ICustomResponse = response.data;
    return customResponse.message;
  }

  static async getRequestMessage(companyId: string, requestId: string): Promise<ICompanyRequestDetail> {
    const response = await HttpService.get<ICompanyRequestDetail>(
      `companies/${companyId}/requests/${requestId}/`,
      {},
      { data: COMPANY_REQUEST_DETAIL_MOCK }
    );
    return response.data;
  }

  static async deleteMembers(companyId: string, memberEmails: string[]): Promise<void> {
    const body = { members: memberEmails };
    await HttpServiceServer.post(`companies/${companyId}/members/unsubscribe`, body, {}, DEFAULT_CUSTOM_RESPONSE_MOCK);
  }

  static async subscribeMembers(companyId: string, planId: string | undefined, members: string[]): Promise<void> {
    const body = {
      planId,
      members
    };
    await HttpServiceServer.post(`companies/${companyId}/members/subscribe`, body, {}, DEFAULT_CUSTOM_RESPONSE_MOCK);
  }

  static async subscribeMembersByFile(companyId: string, requestId: string): Promise<void> {
    const body = { requestId };
    await HttpServiceServer.post(
      `companies/${companyId}/members/subscribe-by-file`,
      body,
      {},
      DEFAULT_CUSTOM_RESPONSE_MOCK
    );
  }

  static async validateSubscriptionFile(args: IValidateFileRequest): Promise<IValidateFileResponse> {
    const bodyB64 = {
      planId: args.planId.toString(),
      isFR: args.isFR
    };
    const body = args.records ? { ...bodyB64, records: args.records } : { ...bodyB64, base64: args.base64 };

    const response = await HttpServiceServer.post<IValidateFileResponse>(
      `/companies/${args.companyId}/members/validate-payroll/${args.countryCode}`,
      body,
      {},
      VALIDATE_PAYROLL_SUCCESS_MOCK_RESPONSE
    );
    return response.data;
  }

  static async downloadSubscriptionTemplate(
    companyId: string,
    countryCode: string,
    subscriptionFR: string
  ): Promise<IFileResponse> {
    const response = await HttpServiceServer.get<IFileResponse>(
      `/companies/${companyId}/members/templates/${countryCode}`,
      { params: { subscriptionFR } },
      MOCK_DOWNLOAD_FILE_RESPONSE
    );
    return response.data;
  }

  static async assingCompanyDomains(planId: string | undefined, domains: string[]): Promise<void> {
    const body = {
      planId,
      domains
    };
    await HttpService.post('companies/assign-domains', body, {}, DEFAULT_CUSTOM_RESPONSE_MOCK);
  }

  static async deleteCompanyDomains(planId: string | undefined, domain: string): Promise<void> {
    const body = {
      planId,
      domain
    };
    await HttpService.post('companies/delete-domains', body, {}, DEFAULT_CUSTOM_RESPONSE_MOCK);
  }

  static async downloadMembersList(companyId: string): Promise<void> {
    await HttpServiceServer.post(`companies/${companyId}/send-members-email`, {}, {}, DEFAULT_CUSTOM_RESPONSE_MOCK);
  }

  static async getActivesInsights(companyId: string): Promise<ICollaboratorInsightsData> {
    const response = await HttpServiceServer.get<ICollaboratorInsightsData>(
      `companies/${companyId}/members/actives-insight/`,
      {},
      { data: COLLABORATOR_INSIGHTS_MOCK_RESPONSE }
    );
    return response.data;
  }

  static async getCollaboratorBenefits(
    companyId: string,
    collaboratorEmail: string
  ): Promise<ICollaboratorBenefitsResponse> {
    const response = await HttpServiceServer.get<ICollaboratorBenefitsResponse>(
      `companies/${companyId}/members/${collaboratorEmail}/benefits`,
      {},
      { data: COLLABORATOR_BENEFITS_MOCK_RESPONSE }
    );
    return response.data;
  }

  static async getCollaboratorDependents(companyId: string, email: string): Promise<ICollaboratorDependents> {
    const response = await HttpServiceServer.get<ICollaboratorDependents>(
      `/companies/${companyId}/members/${email}/dependents`,
      {},
      { data: MOCK_COLLABORATOR_DEPENDENTS }
    );
    return response.data;
  }

  static async getInvitationLink(companyId: string, email: string): Promise<ICollaboratorInvitationLink> {
    const response = await HttpServiceServer.get<ICollaboratorInvitationLink>(
      `companies/${companyId}/members/${email}/invitation-link`,
      {},
      { data: COLLABORATOR_INVITATION_LINK_MOCK_RESPONSE }
    );
    return response.data;
  }

  static async validateMembers(companyId: string, memberEmails: string[]): Promise<IValidateMembersResponse> {
    const response = await HttpServiceServer.post<IValidateMembersResponse>(
      `companies/${companyId}/members/validation`,
      { members: memberEmails },
      {},
      { data: VALIDATE_COLLABORATORS_MOCK_REPONSE }
    );
    return response.data;
  }

  static async getCompanyPlans(companyId: string, locale: string): Promise<ICompanyPlanResponse> {
    const response = await HttpServiceServer.get<ICompanyPlanResponse>(
      `companies/${companyId}/plans`,
      {
        params: { locale: locale }
      },
      { data: MOCK_COMPANY_PLANS }
    );

    return response.data;
  }

  static async getCompanyContract(companyId: string, planId: number): Promise<ICompanyContract> {
    const response = await HttpServiceServer.get<ICompanyContract>(
      `companies/${companyId}/plan/${planId}/contract`,
      {},
      MOCK_COMPANY_CONTRACT
    );
    return response.data;
  }

  static async updateCollaboratorDataRF(data: IUpdateDataRF, companyId: string): Promise<void> {
    const body = {
      ...data
    };
    const response = await HttpServiceServer.patch<void>(
      `companies/${companyId}/members/update_fr_data`,
      body,
      {},
      DEFAULT_CUSTOM_RESPONSE_MOCK
    );

    return response.data;
  }

  static async getCollaboratorDataRF(memberEmail: string, companyId: string): Promise<ICollaboratorDataRF> {
    const response = await HttpServiceServer.get<ICollaboratorDataRF>(
      `companies/${companyId}/members/${memberEmail}/flexible-remuneration`,
      {},
      MOCK_COLLABORATOR_DETAILS_RF_DATA
    );

    return response.data;
  }

  static async getMemberFRCycleConsumptions(
    companyId: string,
    memberEmail: string,
    payrollId: string
  ): Promise<IMemberFRCycleConsumptions> {
    const response = await HttpServiceServer.get<IMemberFRCycleConsumptions>(
      `companies/${companyId}/members/${memberEmail}/flexible-remuneration/payroll-cycles/${payrollId}/consumptions`,
      {},
      { data: MOCK_MEMBER_FR_CYCLE_CONSUMPTIONS }
    );

    return response.data;
  }

  static async getMemberFRCardBalances(companyId: string, memberEmail: string): Promise<IMemberFRCardBalances> {
    const response = await HttpServiceServer.get<IMemberFRCardBalances>(
      `companies/${companyId}/members/${memberEmail}/flexible-remuneration/card-balances`,
      {},
      { data: MOCK_MEMBER_FR_CARD_BALANCES }
    );

    return response.data;
  }

  static async getCollaboratorInsuranceDetail(
    memberEmail: string,
    companyId: string,
    benefitId: string
  ): Promise<ICollaboratorInsuranceDetail> {
    const response = await HttpServiceServer.get<ICollaboratorInsuranceDetail>(
      `companies/${companyId}/members/${memberEmail}/benefits/${benefitId}/insurance-detail`,
      {},
      MOCK_COLLABORATOR_INSURANCE_DETAIL
    );

    return response.data;
  }

  static async downloadDependentsTemplate(companyId: string, countryCode: string): Promise<IFileResponse> {
    const response = await HttpServiceServer.get<IFileResponse>(
      `/companies/${companyId}/members/dependents/templates/${countryCode}`,
      {},
      MOCK_DOWNLOAD_FILE_RESPONSE
    );
    return response.data;
  }

  static async downloadNovationAgreement(companyId: string, memberEmail: string): Promise<IExternalFileResponse> {
    const response = await HttpServiceServer.get<IExternalFileResponse>(
      `/companies/${companyId}/members/${memberEmail}/flexible-remuneration/novation`,
      {},
      { data: MOCK_DOWNLOAD_EXTERNAL_FILE_RESPONSE }
    );
    return response.data;
  }

  static async downloadInsuranceCertificates(companyId: string, memberEmail: string): Promise<IInsuranceCertificate[]> {
    const response = await HttpServiceServer.get<IInsuranceCertificate[]>(
      `/companies/${companyId}/members/${memberEmail}/insurance-certificate`,
      {},
      { data: MOCK_INSURANCE_CERTIFICATES }
    );
    return response.data;
  }

  static async validateDependentsFile(args: IValidateDependentsFileRequest): Promise<IValidateDependentsFileResponse> {
    const body = args.records ? { records: args.records } : { base64: args.base64 };

    const response = await HttpServiceServer.post<IValidateDependentsFileResponse>(
      `/companies/${args.companyId}/members/dependents/validate-file/${args.countryCode}`,
      body,
      {},
      MOCK_DEPENDENTS_BY_FILE_VALIDATION
    );
    return response.data;
  }

  static async addDependentsByFile(companyId: string, requestId: string): Promise<void> {
    const body = { requestId };
    await HttpServiceServer.post(
      `companies/${companyId}/members/dependents/subscribe`,
      body,
      {},
      DEFAULT_CUSTOM_RESPONSE_MOCK
    );
  }
}

const Companies = CollaboratorsCompanies;

export default Companies;
